/**
 * KAN-592: pure geometry for placing the notification panel.
 *
 * THE DEFECT THIS EXISTS TO KILL. The panel used to be a DaisyUI
 * `dropdown-end` `dropdown-content` with a hard `w-80` (20rem = 320px) and no
 * viewport cap. `dropdown-end` right-anchors the panel to the TRIGGER's right
 * edge, so the panel's left edge lands at exactly `bellRight - 320`. The bell
 * is NOT at the right edge of the header: in `main-layout.component.html` it
 * sits before `<app-help-button>`, and `<app-organization-switcher>` carries
 * `order-last grow basis-44`, so the switcher wraps to its own row at narrow
 * widths and sits BESIDE the icons at wider ones - which pushes the bell
 * FURTHER left as the viewport gets WIDER. That is why the reporter measured
 * 430px as worse than 320px. `bellRight - 320` is negative at every width in
 * the report, and left overflow creates no horizontal page scroll, so the
 * app-shell overflow guard (`e2e/tests/app-shell-horizontal-overflow.spec.ts`)
 * could never see it.
 *
 * TWO FAULTS, TWO FIXES, BOTH REQUIRED:
 *   1. `w-80` is a hard 320px. At a 320px viewport the panel alone is the whole
 *      screen. Fixed by capping the width to `viewport.width - 2*margin`.
 *   2. A width cap ALONE is not enough. At 430px with the bell at x=250, a
 *      320px-capped panel right-anchored to the bell still starts at -70. The
 *      LEFT EDGE has to be clamped to the viewport. Both halves are
 *      individually mutated red in `notification-panel-position.spec.ts`.
 *
 * The arithmetic itself lives in
 * `src/app/shared/utils/anchored-panel-position.utils.ts`, shared with the help
 * menu, which had the identical defect (KAN-619). This file only supplies the
 * bell's preferred width.
 *
 * Pure and DOM-free on purpose. jsdom has no layout engine, so a
 * `getBoundingClientRect()` assertion in a component spec is a zero rect and
 * can never fail - a guard that reads as coverage while detecting nothing
 * (the mechanism behind the three false PASS verdicts on KAN-244). Doing the
 * arithmetic here means the numbers in the spec are REAL numbers and a broken
 * clamp genuinely goes red. The real-browser guard that proves this arithmetic
 * is actually reaching the rendered panel is
 * `e2e/tests/notification-panel-viewport-clamp.spec.ts`.
 */

import {
  ANCHORED_PANEL_MARGIN,
  AnchoredPanelPlacement,
  TriggerRect,
  Viewport,
  computeAnchoredPanelPlacement,
} from '../../shared/utils/anchored-panel-position.utils';

export type { TriggerRect, Viewport };

export type NotificationPanelPlacement = AnchoredPanelPlacement;

/** Gap between the trigger and the panel, and the minimum padding kept from any viewport edge. */
export const NOTIFICATION_PANEL_MARGIN = ANCHORED_PANEL_MARGIN;

/** The comfortable reading width the panel uses whenever the viewport allows it (the old `w-80`). */
export const NOTIFICATION_PANEL_PREFERRED_WIDTH = 320;

/**
 * Places the panel below the bell, right-anchored to the bell (preserving the
 * original `dropdown-end` look wherever there is room for it), then clamps it
 * so it is ALWAYS fully inside the viewport regardless of where in the header
 * the bell happens to sit.
 */
export function computeNotificationPanelPlacement(
  trigger: TriggerRect,
  viewport: Viewport,
  options: { margin?: number; preferredWidth?: number } = {},
): NotificationPanelPlacement {
  return computeAnchoredPanelPlacement(trigger, viewport, {
    margin: options.margin,
    preferredWidth: options.preferredWidth ?? NOTIFICATION_PANEL_PREFERRED_WIDTH,
  });
}
